import React, { useEffect, useState } from 'react'
import { getMediaUrl } from '../helpers/utils'
import {
    Container,
    Card,
    Image,
    Text,
    Group,
    Badge,
    Box,
    SimpleGrid,
    Divider,
    Loader,
    Center,
    ThemeIcon,
    Button
} from '@mantine/core'
import axios from '../helpers/Api'

export const SharedPropertyView = () => {
    const pathSegments = window.location.pathname.split('/')
    const token = pathSegments[pathSegments.indexOf('p') + 1]

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [activeImage, setActiveImage] = useState(0)

    useEffect(() => {
        const fetchSharedProperty = async () => {
            try {
                const response = await axios.get(`/shared/${token}`)

                if (response.data.success) {
                    setData(response.data.data)
                } else {
                    setError("Ce lien est invalide ou a expiré.")
                }
            } catch (err) {
                console.error("Erreur chargement bien partagé :", err)
                setError("Ce lien de partage est introuvable ou a expiré (validité 48h).")
            } finally {
                setLoading(false)
            }
        }

        if (token) {
            fetchSharedProperty()
        } else {
            setLoading(false)
            setError("Aucun identifiant de partage trouvé.")
        }
    }, [token])

    if (loading) {
        return (
            <Center style={{ height: '100vh', backgroundColor: '#f8f9fa' }}>
                <Loader size="xl" color="blue" />
            </Center>
        )
    }

    if (error || !data) {
        return (
            <Center style={{ height: '100vh', flexDirection: 'column', backgroundColor: '#f8f9fa', padding: '20px' }}>
                <ThemeIcon color="red" size={80} radius="xl" mb="md">
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: '40px' }} />
                </ThemeIcon>
                <Text weight={700} size="xl" align="center" mb="sm">Oups ! Lien indisponible</Text>
                <Text color="dimmed" align="center" maw={400}>{error}</Text>
            </Center>
        )
    }

    const { property, agency, expiresAt } = data

    return (
        <Box style={{ backgroundColor: '#f4f6f8', height: '100vh', overflowY: 'auto', paddingBottom: '50px' }}>
            {/* HEADER AGENCE */}
            <Box style={{ backgroundColor: 'white', borderBottom: '1px solid #e9ecef', padding: '15px 0' }} mb="xl">
                <Container size="md">
                    <Group position="apart" align="center">
                        <Group spacing="sm">
                            {agency?.logo && (
                                <img
                                    src={getMediaUrl(agency.logo)}
                                    alt="Logo Agence"
                                    style={{ height: '45px', objectFit: 'contain' }}
                                />
                            )}
                            <div>
                                <Text weight={700} size="md">{agency?.companyName}</Text>
                                <Text size="xs" color="dimmed">{agency?.email}</Text>
                            </div>
                        </Group>
                        {agency?.mobile && (
                            <Button
                                component="a"
                                href={`tel:${agency.mobile}`}
                                variant="light"
                                color="green"
                                leftIcon={<i className="fas fa-phone-alt" />}
                            >
                                {agency.mobile}
                            </Button>
                        )}
                    </Group>
                </Container>
            </Box>

            {/* CONTENU PRINCIPAL */}
            <Container size="md">
                <Card shadow="sm" p="lg" radius="md" withBorder>

                    {/* TITRE ET METADONNÉES */}
                    <Box mb="md">
                        <Badge color="blue" mb={5}>Réf : {property.reference}</Badge>
                        <Text weight={700} size="xl">{property.type}</Text>

                        <Group spacing="md" mt={8}>
                            <Group spacing="xs">
                                <ThemeIcon color="grape" variant="light" size="sm">
                                    <i className="fas fa-map-marker-alt" />
                                </ThemeIcon>
                                <Text size="sm" weight={500}>
                                    {property.location?.wilaya}, {property.location?.commune}
                                </Text>
                            </Group>

                            {property.superficie && (
                                <Group spacing="xs">
                                    <ThemeIcon color="cyan" variant="light" size="sm">
                                        <i className="fas fa-expand-arrows-alt" />
                                    </ThemeIcon>
                                    <Text size="sm" weight={500}>{property.superficie}</Text>
                                </Group>
                            )}
                        </Group>
                    </Box>

                    <Divider my="sm" />

                    {/* GALERIE PHOTOS */}
                    {property.photos && property.photos.length > 0 ? (
                        <Box mb="xl">
                            <Image
                                src={getMediaUrl(property.photos[activeImage].path)}
                                height={400}
                                radius="md"
                                withPlaceholder
                                alt="Vue principale"
                                style={{ objectFit: 'cover' }}
                            />
                            {property.photos.length > 1 && (
                                <SimpleGrid cols={5} spacing="xs" mt="sm">
                                    {property.photos.map((photo, index) => (
                                        <div
                                            key={index}
                                            onClick={() => setActiveImage(index)}
                                            style={{
                                                cursor: 'pointer',
                                                border: activeImage === index ? '2px solid #228be6' : '2px solid transparent',
                                                borderRadius: '6px',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Image src={getMediaUrl(photo.path)} height={70} withPlaceholder />
                                        </div>
                                    ))}
                                </SimpleGrid>
                            )}
                        </Box>
                    ) : (
                        <Box p="xl" style={{ backgroundColor: '#f8f9fa', textAlign: 'center', borderRadius: '8px' }} mb="xl">
                            <Text color="dimmed">Aucune photo disponible pour ce bien.</Text>
                        </Box>
                    )}

                    {/* DESCRIPTION CLIENT (Anciennement Informations Supplémentaires) */}
                    {property.addInfos && (
                        <>
                            <Divider my="md" />
                            <Box mb="md">
                                <Text weight={700} size="md" mb="xs">Description du bien</Text>
                                <Text size="sm" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                    {property.addInfos}
                                </Text>
                            </Box>
                        </>
                    )}

                    <Divider my="xl" />

                    {/* FOOTER / EXPIRATION */}
                    <Group position="apart">
                        <Text size="xs" color="dimmed">
                            Ce lien sécurisé expire le : {new Date(expiresAt).toLocaleDateString()} à {new Date(expiresAt).toLocaleTimeString()}
                        </Text>
                        <Text size="xs" color="dimmed" weight={600}>
                            Propulsé par Wakala Pro Tech
                        </Text>
                    </Group>

                </Card>
            </Container>
        </Box>
    )
}